import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FlaskConical, LogOut } from 'lucide-react';
import { BUILD, buildLabel } from '@/lib/buildInfo';
import { exitPreviewHref, isPreviewSite } from './previewSite';

/**
 * "You are on the preview site" (2026-09-28, Michael: "I need a exit site
 * preview button and something that clearly shows you are in a site
 * preview"). preview.nadc1.net serves a branch's frontend against the LIVE
 * backend, so a person there can approve real time, send real mail, move
 * real equipment — while looking at a menu that may not match what the rest
 * of the company sees. Hence a band that cannot be missed on every screen,
 * naming the build and the branch, with one way out that lands on the same
 * page of the live site.
 *
 * The signal is the host name, not the build: a master build promoted to the
 * preview host is still "the preview site", and a branch build served live
 * (it never is) would be the live site. Adds `has-preview-banner` to <body>
 * so the app shells (h-screen) shrink by the band's height — the same
 * mechanism the shared navbar uses (styles/index.css).
 */

export function PreviewBanner() {
  const location = useLocation();
  const on = isPreviewSite();

  useEffect(() => {
    if (!on) return undefined;
    document.body.classList.add('has-preview-banner');
    if (!document.title.startsWith('[PREVIEW]')) document.title = `[PREVIEW] ${document.title}`;
    return () => { document.body.classList.remove('has-preview-banner'); };
  }, [on]);

  if (!on) return null;
  const branch = BUILD.branch && BUILD.branch !== 'HEAD' && BUILD.branch !== 'master' ? BUILD.branch : null;

  return (
    <div
      role="status"
      aria-label="Site preview"
      data-testid="preview-banner"
      className="flex h-9 shrink-0 items-center justify-between gap-3 border-b-2 border-amber-300 bg-amber-500 px-3 text-xs text-graphite-950"
    >
      <span className="flex min-w-0 items-center gap-2">
        <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
        <span className="font-bold uppercase tracking-wider">Site preview</span>
        <span className="hidden truncate sm:inline">
          — a build under review, not the live site. {buildLabel()}{branch ? ` · ${branch}` : ''}. Anything you save here is saved for real.
        </span>
      </span>
      <a
        href={exitPreviewHref(location)}
        className="flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-graphite-950 px-3 text-xs font-semibold text-amber-300 hover:bg-graphite-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-graphite-950"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        Exit preview
      </a>
    </div>
  );
}
